<template>
  <div class="relative w-full h-full">
    <!-- Loader -->
    <div v-if="!imageSrc" class="flex justify-center item-center p-3">
      <image-loader @loaded="onImageLoaded">
        {{ btnLabel }}
      </image-loader>
    </div>
    <!-- Main flow -->
    <div v-else class="">
      <full-screen v-if="isTouchOnly">
        <transition-group :name="animationClass" @after-enter="handleCropperTransitionEnd" class="w-full h-full">
          <div v-show="!compositeImageSrc" key="cropper-container" class="relative  w-full h-full">
            <image-cropper ref="cropperComponentRef" :src="imageSrc" :default-aspect="4 / 5" :overlay-data="overlayData"
              :bottom-padding="bottomPadding" :top-padding="topPadding" @cancel="onCancel" @preview="onPreviewImage"
              @ready="onCropperReady" @change="onCropperChange" />
          </div>
          <div v-show="compositeImageSrc" key="preview" class="relative w-full h-full">
            <image-preview key="preview" :image="compositeImageSrc" :file-name="fileName"
              :bottom-padding="bottomPadding" :top-padding="topPadding" @cancel="onCancel" @crop="onCrop" />
          </div>
        </transition-group>
      </full-screen>

      <!-- Desktop: modal -->
      <AppModal v-else :show="true">

        <div v-show="!compositeImageSrc" key="cropper-container" class="relative w-full h-full">
          <image-cropper ref="cropperComponentRef" :src="imageSrc" :default-aspect="4 / 5" :overlay-data="overlayData"
            @cancel="onCancel" @preview="onPreviewImage" @ready="onCropperReady" @change="onCropperChange" />
        </div>
        <div v-show="compositeImageSrc" key="preview" class="relative w-full h-full">
          <image-preview key="preview" :image="compositeImageSrc" :file-name="fileName" @cancel="onCancel"
            @crop="onCrop" />
        </div>
      </AppModal>
    </div>
    <div @if="error">
      <p>{{ error }}</p>
    </div>
    <div id="modals"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, provide } from 'vue';
import { useOverlay, type ResultData, type OverlayData } from '@/composables/useOverlay';
import { useIsTouchOnly } from '@/composables/useIsTouchOnly'
import { useAnalytics, trackEventKey, marathonNameKey } from '@/composables/useAnalytics'
import { type CropperResult, type Coordinates } from 'vue-advanced-cropper';
import { svgToCanvas } from '@/utils/svgToCanvas';
import ImageLoader from '@/components/ImageLoader.vue'
import ImageCropper, { type CropperEvent } from './ImageCropper.ce.vue';
import ImagePreview from './ImagePreview.vue'
import FullScreen from './FullScreen.vue';
import AppModal from './AppModal.vue';
const imageSrc = ref<string | null>(null)
const compositeImageSrc = ref<string | null>(null);
const isReset = ref(false)
const error = ref('')

const props = defineProps<{
  results: ResultData,
  btnLabel?: string,
  mainText: string,
  topPadding?: number,
  bottomPadding?: number,
}>()

const latestMarathon = computed(() => props.results.results[props.results.results.length - 1]?.marathon ?? '')
const { trackEvent } = useAnalytics()
provide(trackEventKey, trackEvent)
provide(marathonNameKey, latestMarathon.value)

function handleViewportResize() {
  console.log('handleViewportResize', cropperComponentRef.value ? 'has ref' : 'no ref')
  if (cropperComponentRef.value) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cropperComponentRef.value?.manualReset()
        isReset.value = true
      })
    })
  }
}

onMounted(() => {
  console.log('👻 Component Attached: Instance ID:', Math.random().toString(36).substr(2, 9))
  window.addEventListener('resize', handleViewportResize)
  trackEvent('wmm_widget_opened', { event_category: 'wmm_share_widget', marathon_name: latestMarathon.value })
})

onUnmounted(() => {
  console.log('⚰️ Component Detached: Cleaning up...')
  window.removeEventListener('resize', handleViewportResize)
})

const overlayData = computed((): OverlayData => {
  return {
    showName: true,
    mainText: props.mainText,
    results: props.results
  }
});

const fileName = computed(() => {
  const latestResult = props.results.results[props.results.results.length - 1]
  return `${latestResult?.marathon}-${latestResult?.date}.png`
})


const animationClass = computed(() => {
  return compositeImageSrc.value ? 'slide-left' : 'slide-right'
})

import type { ComponentPublicInstance } from 'vue';

type CropperComponentInstance = ComponentPublicInstance<{
  manualRefresh: () => void,
  manualReset: () => void,
}>;

const cropperComponentRef = ref<CropperComponentInstance | null>(null);

function handleCropperTransitionEnd() {
  // Check if the cropper component is mounted and if we are in the cropper view
  // The 'manualRefresh' function is available because we defined it with defineExpose()
  // console.log("Transition isReset:", isReset.value, "compositeImageSrc", compositeImageSrc.value ? 'true' : 'false');

  // If we are moving from the preview to the edit screen after a device rotation we need to refresh the cropper.
  // We don't do this every time because this will loose the current crop.
  if (isReset.value && !compositeImageSrc.value && cropperComponentRef.value) {
    cropperComponentRef.value.manualRefresh()
    isReset.value = false
    // console.log("Cropper refreshed after transition.", currentCoordinates.value);
  }
}

const { isTouchOnly } = useIsTouchOnly()
const cropperReady = ref(false)

function onCropperReady() {
  console.log('*** cropper ready')
  if (cropperComponentRef.value) {
    cropperComponentRef.value.manualRefresh();
  }
  cropperReady.value = true;
}

const currentCoordinates = ref<Coordinates | undefined>(undefined)

function onCropperChange(e: CropperEvent) {
  console.log('*** cropper change', e)
  if (e.coordinates.width !== 0 && e.coordinates.height !== 0) {
    currentCoordinates.value = e.coordinates
  }
}

function onImageLoaded(evt: { blobUrl: string, file: File }) {
  const reader = new FileReader();
  reader.onload = () => {
    imageSrc.value = evt.blobUrl
    trackEvent('wmm_image_uploaded', { event_category: 'wmm_share_widget', marathon_name: latestMarathon.value, file_type: evt.file.type })
  };
  reader.readAsArrayBuffer(evt.file);
}

function onCrop() {
  cropperReady.value = false;
  compositeImageSrc.value = null
}

function onCancel() {
  imageSrc.value = null;
  compositeImageSrc.value = null;
}

async function onPreviewImage(result: CropperResult) {

  const { canvas, coordinates } = result;

  if (canvas) {
    await document.fonts.load("100 1px 'Elza Condensed'");
    await document.fonts.load("800 1px 'Elza Condensed bold'");

    // IOS font load issues
    await document.fonts.ready;
    await new Promise(requestAnimationFrame); // Yield one frame (critical on iOS)

    const currentSize = ref({ width: coordinates.width, height: coordinates.height });
    const { svgTemplate } = useOverlay(currentSize, overlayData.value);
    const svgString = svgTemplate.value;

    try {

      const { dataUrl } = await svgToCanvas(canvas, svgString, 1080);
      compositeImageSrc.value = dataUrl;
      trackEvent('wmm_composite_generated', { event_category: 'wmm_share_widget', marathon_name: latestMarathon.value })
    } catch (error) {
      console.error('onPreviewImag error', error)
    }
  };

}


</script>

<style>
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.5s ease;
  /* Adjust duration and easing as needed */
  position: absolute;
  /* Ensures elements don't affect each other's flow during transition */
  width: 100%;
  height: 100%;
  /* Important for absolute positioning */
}

/* 2. Entering transition: Starts off-screen to the right and slides in */
.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-left-enter-to {
  opacity: 1;
  transform: translateX(0);
}

/* 3. Leaving transition: Slides out to the left */
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.5s ease;
  /* Adjust duration and easing as needed */
  position: absolute;
  /* Ensures elements don't affect each other's flow during transition */
  width: 100%;
  height: 100%;
  /* Important for absolute positioning */
}

/* 2. Entering transition: Starts off-screen to the left and slides in */
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-right-enter-to {
  opacity: 1;
  transform: translateX(0);
}

/* 3. Leaving transition: Slides out to the right */
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
